if (!customElements.get('routine-builder')) {
  class RoutineBuilder extends HTMLElement {
    constructor() {
      super();
      this._initialized = false;
      this._lastScrollY = window.scrollY;
      this._scrollTimeout = null;
      this._countObserver = null;
      this._selectedProducts = [];
      this._totalOriginalPrice = 0;
      this._maxSlots = 6;
      this._stepDiscounts = {};
      this._i18n = {};
      // DOM refs — populated in _init
      this._slotsContainer = null;
      this._bundleCountEl = null;
      this._originalPriceEl = null;
      this._discountedPriceEl = null;
      this._addToCartBtn = null;
      this._routineError = null;
      // Bind handlers that need to be removable
      this._onScroll = this._onScroll.bind(this);
      if (Shopify.designMode) {
        window.addEventListener('shopify:section:load', this._init.bind(this));
      }
    }

    connectedCallback() {
      this._init();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this._onScroll);
      if (this._countObserver) {
        this._countObserver.disconnect();
        this._countObserver = null;
      }
      clearTimeout(this._scrollTimeout);
    }

    _init() {
      if (this._initialized) return;
      this._initialized = true;

      // Parse config from embedded JSON blocks
      const discountsEl = this.querySelector('[data-routine-discounts]');
      const routineDiscountsData = discountsEl
        ? JSON.parse(discountsEl.textContent)
        : { maxSlots: 6, discounts: {} };
      this._maxSlots = routineDiscountsData.maxSlots;
      this._stepDiscounts = routineDiscountsData.discounts;

      const i18nEl = this.querySelector('[data-routine-i18n]');
      this._i18n = i18nEl ? JSON.parse(i18nEl.textContent) : {};

      // Cache DOM refs
      this._slotsContainer = this.querySelector('.routine-summary__slots-container');
      this._bundleCountEl = this.querySelector('.routine-summary__count');
      this._originalPriceEl = this.querySelector('.routine-summary__original-price');
      this._discountedPriceEl = this.querySelector('.routine-summary__discounted-price');
      this._addToCartBtn = this.querySelector('.routine-summary__cart-button');
      this._routineError = this.querySelector('.routine-summary__error');

      this._selectedProducts = [];
      this._totalOriginalPrice = 0;

      this._initCollapsibleSections();
      this._initMobileScrollBehavior();

      // Toggle-button listeners
      this.querySelectorAll('.routine-step__toggle-button').forEach(button => {
        button.addEventListener('click', (event) => {
          event.stopPropagation();
          const parentSection = button.closest('.routine-step');
          this._toggleStep(parentSection);
        });
        const parentSection = button.closest('.routine-step');
        this._updateStepButtonText(parentSection);
      });

      // P1: event delegation for "Aggiungi alla routine" buttons
      this.addEventListener('click', (e) => {
        const button = e.target.closest('.routine-product__select-button');
        if (!button || !this.contains(button)) return;

        const card = button.closest('.card-product-wrapper')
          ?.querySelector('product-card[data-routine-mode]');

        let resolvedVariant = null;
        if (card && Array.isArray(card.variantsObj)) {
          const vid = card.findCurrentVariantFromOptionRadioInputs?.().currentVariantId;
          if (vid) {
            resolvedVariant = card.variantsObj.find(v => v.id === vid);
          }
          if (!resolvedVariant) {
            const checked = [...card.querySelectorAll('.variant-option-radio-input:checked')].map(r => r.value);
            resolvedVariant = card.variantsObj.find(v => v.options.join() === checked.join());
          }
        }

        let variantImage = button.dataset.productImage;
        const variantImageSrc = resolvedVariant && resolvedVariant.featured_image && resolvedVariant.featured_image.src;
        if (variantImageSrc) {
          variantImage = variantImageSrc + (variantImageSrc.includes('?') ? '&' : '?') + 'width=200&height=200&crop=center';
        }

        const productData = {
          id: button.dataset.productId,
          variantId: resolvedVariant ? String(resolvedVariant.id) : button.dataset.variantId,
          title: button.dataset.productTitle,
          image: variantImage,
          price: resolvedVariant ? resolvedVariant.price / 100 : parseFloat(button.dataset.productPrice || '0'),
          stepIndex: button.dataset.stepIndex,
          selectId: `${button.dataset.productId}-${Date.now()}`
        };

        const added = this._addProductToRoutine(productData);
        if (added) {
          button.dataset.selectId = productData.selectId;
        }
      });

      // P3: event delegation for product-card preview clicks → open ext-product-details-drawer (REQ-03)
      this.addEventListener('click', (e) => {
        const isMediaLink = e.target.closest('.product-card__link--routine');
        const isTitleSpan = e.target.closest('.product-card__title span');
        if (!isMediaLink && !isTitleSpan) return;

        e.preventDefault();

        const url = e.target.closest('[data-product-url]')?.dataset.productUrl;
        const stepIndex = e.target.closest('[data-step-index]')?.dataset.stepIndex;

        if (!url || stepIndex === undefined || stepIndex === null) {
          console.error(
            '[routine-builder] product card click: missing data-product-url or data-step-index on click target ancestors.',
            { url, stepIndex, target: e.target }
          );
          return;
        }

        const drawer = document.querySelector('ext-product-details-drawer');
        if (!drawer) {
          console.error('[routine-builder] ext-product-details-drawer element not found in DOM.');
          return;
        }

        drawer.open(url, parseInt(stepIndex, 10));
      });

      // P2: ext:add-to-routine — drawer fires this; funnel into the single-source add flow (REQ-08)
      document.addEventListener('ext:add-to-routine', (e) => {
        this._addProductToRoutine(e.detail);
      });

      // P7: add-to-cart handler
      if (this._addToCartBtn) {
        this._addToCartBtn.addEventListener('click', () => {
          if (this._selectedProducts.length === 0) return;

          const items = this._selectedProducts.map(product => ({
            id: product.variantId,
            quantity: 1
          }));

          if (this._selectedProducts && this._selectedProducts.length > 0) {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
              event: 'routine_completed',
              ecommerce: {
                items: this._selectedProducts
                  .filter(product => product && product.id)
                  .map(product => ({
                    item_id: product.id || '',
                    item_variant: product.variantId || '',
                    item_name: product.title || 'Prodotto sconosciuto',
                    price: parseFloat(product.price || '0'),
                    step_index: product.stepIndex || '0'
                  })),
                value: this._selectedProducts.reduce((total, product) => {
                  return total + (parseFloat(product.price || '0') || 0);
                }, 0),
                item_count: this._selectedProducts.length
              }
            });
          }

          fetch(window.Shopify.routes.root + 'cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items })
          })
          .then(response => {
            if (!response.ok) return response.json().then(err => Promise.reject(err));
            return response.json();
          })
          .then(() => {
            this._hideRoutineError();
            if (typeof window.openCartDrawer === 'function') {
              window.openCartDrawer();
            } else {
              window.location.href = '/cart';
            }
          })
          .catch(error => {
            console.error('Error adding to cart:', error);
            this._showRoutineError(error && (error.description || error.message) || this._i18n.cartError);
          });
        });
      }
    }

    // ===== Collapsible sections =====

    _initCollapsibleSections() {
      const allSections = this.querySelectorAll('.routine-step');

      allSections.forEach((section, index) => {
        if (index === 0) {
          section.classList.add('expanded');
        } else {
          section.classList.remove('expanded');
        }
      });
    }

    _toggleStep(stepElement) {
      const isExpanded = stepElement.classList.contains('expanded');
      if (isExpanded) {
        stepElement.classList.remove('expanded');
      } else {
        stepElement.classList.add('expanded');
      }
      this._updateStepButtonText(stepElement);
    }

    _updateStepButtonText(stepElement) {
      const button = stepElement.querySelector('.routine-step__toggle-button');
      if (!button) return;
      const isExpanded = stepElement.classList.contains('expanded');
      if (window.innerWidth < 750) {
        button.innerHTML = isExpanded ? '&minus;' : '+';
      } else {
        button.textContent = isExpanded ? this._i18n.closeStep : this._i18n.openStep;
      }
    }

    // ===== Mobile scroll behavior =====

    _initMobileScrollBehavior() {
      if (window.innerWidth > 749) return;

      const routineSummary = this.querySelector('.routine-summary');
      const bundleInfo = routineSummary?.querySelector('.routine-summary__bundle-info');

      if (!routineSummary || !bundleInfo) return;

      this._lastScrollY = window.scrollY;
      routineSummary.classList.add('routine-summary--collapsed');

      window.addEventListener('scroll', this._onScroll);

      bundleInfo.addEventListener('click', () => {
        if (routineSummary.classList.contains('routine-summary--collapsed')) {
          routineSummary.classList.remove('routine-summary--collapsed');
          routineSummary.classList.add('routine-summary--expanded');
        } else {
          routineSummary.classList.add('routine-summary--collapsed');
          routineSummary.classList.remove('routine-summary--expanded');
        }
        this._updateToggleButton(routineSummary);
      });

      this._updateToggleButton(routineSummary);

      const countElement = routineSummary.querySelector('.routine-summary__count');
      if (countElement) {
        let lastCount = parseInt(countElement.textContent) || 0;

        this._countObserver = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            if (mutation.type === 'childList' || mutation.type === 'characterData') {
              const currentCount = parseInt(countElement.textContent) || 0;
              if (currentCount > lastCount) {
                routineSummary.classList.remove('routine-summary--collapsed');
                routineSummary.classList.add('routine-summary--expanded');
                this._updateToggleButton(routineSummary);
              }
              lastCount = currentCount;
            }
          });
        });

        this._countObserver.observe(countElement, {
          childList: true,
          characterData: true,
          subtree: true
        });
      }
    }

    _onScroll() {
      const routineSummary = this.querySelector('.routine-summary');
      if (!routineSummary) return;

      const currentScrollY = window.scrollY;
      if (Math.abs(currentScrollY - this._lastScrollY) > 5) {
        routineSummary.classList.add('routine-summary--collapsed');
        routineSummary.classList.remove('routine-summary--expanded');
      }
      this._lastScrollY = currentScrollY;

      clearTimeout(this._scrollTimeout);
      this._scrollTimeout = setTimeout(() => {
        // scroll settled — no action needed (isScrolling flag was dead code)
      }, 150);
    }

    _updateToggleButton(routineSummary) {
      const toggleButton = routineSummary.querySelector('.routine-summary__toggle-button');
      if (!toggleButton) return;
      const isExpanded = routineSummary.classList.contains('routine-summary--expanded');
      toggleButton.innerHTML = isExpanded ? '&minus;' : '+';
    }

    // ===== Product management =====

    _addProductToRoutine(productData) {
      if (this._selectedProducts.length >= this._maxSlots) {
        alert('Hai già raggiunto il numero massimo di prodotti per la tua routine.');
        return false;
      }

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'routine_product_selected',
        ecommerce: {
          items: [{
            item_id: productData.id,
            item_variant: productData.variantId,
            item_name: productData.title,
            price: parseFloat(productData.price || '0'),
            step_index: productData.stepIndex || '0'
          }]
        }
      });

      this._selectedProducts.push(productData);

      const emptySlot = this._findEmptySlot();
      if (emptySlot) {
        this._fillSlot(emptySlot, productData);
      }

      this._updateSummary();
      return true;
    }

    _removeProductFromSlot(slotElement) {
      const slotIndex = parseInt(slotElement.dataset.slotIndex);
      const productId = slotElement.dataset.productId;

      if (productId) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'routine_product_removed',
          ecommerce: {
            items: [{
              item_id: productId || '',
              item_variant: slotElement.dataset.variantId || '',
              item_name: slotElement.dataset.productTitle || 'Prodotto sconosciuto',
              price: parseFloat(slotElement.dataset.productPrice || '0'),
              step_index: slotElement.dataset.stepIndex || '0'
            }]
          }
        });
      }

      const productIndex = this._selectedProducts.findIndex(p => p.id === productId);
      if (productIndex > -1) {
        this._selectedProducts.splice(productIndex, 1);
      }

      const productButton = this.querySelector(`.routine-product__select-button[data-product-id="${productId}"][data-select-id="${slotElement.dataset.selectId}"]`);
      if (productButton) {
        delete productButton.dataset.selectId;
      }

      this._resetAllSlots();
      this._redistributeProducts();
      this._updateSummary();
    }

    _resetAllSlots() {
      const allSlots = this.querySelectorAll('.routine-summary__slot');
      allSlots.forEach(slot => {
        const slotIndex = parseInt(slot.dataset.slotIndex);
        const stepDiscount = this._stepDiscounts[slotIndex] || 0;

        if (stepDiscount > 0) {
          slot.innerHTML = `<span class="routine-summary__discount-text">${this._i18n.getDiscount} -${stepDiscount}%</span>`;
          slot.classList.add('routine-summary__slot--discount');
        } else {
          slot.innerHTML = '<span class="routine-summary__empty-slot-indicator"></span>';
          slot.classList.remove('routine-summary__slot--discount');
        }

        slot.classList.remove('routine-summary__slot--filled');
        delete slot.dataset.productId;
        delete slot.dataset.selectId;
      });
    }

    _redistributeProducts() {
      if (this._selectedProducts.length === 0) return;
      const allSlots = Array.from(this.querySelectorAll('.routine-summary__slot'));
      this._selectedProducts.forEach((product, index) => {
        if (index < allSlots.length) {
          this._fillSlot(allSlots[index], product);
        }
      });
    }

    _updateSummary() {
      this._bundleCountEl.textContent = this._selectedProducts.length;

      this._totalOriginalPrice = this._selectedProducts.reduce((total, product) => {
        return total + parseFloat(product.price || 0);
      }, 0);

      let discountPercent = 0;
      const productCount = this._selectedProducts.length;
      for (let i = 1; i <= 10; i++) {
        if (productCount >= i && this._stepDiscounts[i] > discountPercent) {
          discountPercent = this._stepDiscounts[i];
        }
      }

      if (discountPercent > 0) {
        const discountedPrice = this._totalOriginalPrice * (1 - discountPercent / 100);
        this._originalPriceEl.textContent = this._formatPrice(this._totalOriginalPrice);
        this._discountedPriceEl.textContent = this._formatPrice(discountedPrice);
        this._discountedPriceEl.classList.add('routine-summary__discounted-price--with-discount');
      } else {
        this._originalPriceEl.textContent = '';
        this._discountedPriceEl.textContent = this._formatPrice(this._totalOriginalPrice);
        this._discountedPriceEl.classList.remove('routine-summary__discounted-price--with-discount');
      }

      this._addToCartBtn.disabled = this._selectedProducts.length === 0;
    }

    // ===== Slot helpers =====

    _findEmptySlot() {
      return Array.from(this._slotsContainer.querySelectorAll('.routine-summary__slot:not(.routine-summary__slot--filled)')).shift();
    }

    _fillSlot(slotElement, productData) {
      slotElement.innerHTML = `
        <img src="${productData.image}" alt="${productData.title}" loading="lazy">
        <button type="button" class="routine-summary__remove-button" aria-label="Rimuovi ${productData.title}">✕</button>
      `;
      slotElement.classList.add('routine-summary__slot--filled');
      slotElement.classList.remove('routine-summary__slot--discount');
      slotElement.dataset.productId = productData.id;
      slotElement.dataset.selectId = productData.selectId;
      slotElement.dataset.variantId = productData.variantId;
      slotElement.dataset.productTitle = productData.title;
      slotElement.dataset.productImage = productData.image;
      slotElement.dataset.productPrice = productData.price;
      slotElement.dataset.stepIndex = productData.stepIndex;
      slotElement.dataset.sku = productData.sku;
      slotElement.dataset.productUrl = productData.url;
      slotElement.dataset.imageUrl = productData.imageUrl;

      const img = slotElement.querySelector('img');
      if (img) {
        img.classList.add('lazy');
        img.addEventListener('load', function() {
          setTimeout(() => {
            img.removeAttribute('loading');
            img.classList.remove('lazy');
          }, 100);
        });
        if (img.complete) {
          setTimeout(() => {
            img.removeAttribute('loading');
            img.classList.remove('lazy');
          }, 100);
        }
      }

      const removeButton = slotElement.querySelector('.routine-summary__remove-button');
      if (removeButton) {
        removeButton.addEventListener('click', (event) => {
          event.stopPropagation();
          this._removeProductFromSlot(slotElement);
        });
      }
    }

    _formatPrice(amount) {
      return '€' + amount.toFixed(2);
    }

    // ===== Error display =====

    _showRoutineError(msg) {
      if (!this._routineError) return;
      this._routineError.textContent = msg;
      this._routineError.hidden = false;
    }

    _hideRoutineError() {
      if (!this._routineError) return;
      this._routineError.hidden = true;
      this._routineError.textContent = '';
    }
  }

  customElements.define('routine-builder', RoutineBuilder);
}
